"use client";
import { useLocaleCtx } from "@/contexts/locale";
import { useTranslation } from "@/i18n/client";
import WINNERS from "@/i18n/locales/en/winners.json";
import { getGalleryPageWinners } from "@/services/gallery/gallery.client";
import { filter, Winner } from "@/types/gallery";
import { Categories } from "@/types/winners";
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useSearchParams } from "next/navigation";

interface WinnersContextProps {
  winners: any[];
  loading: boolean;
  pageNo: number;
  pageSize: number;
  year: filter;
  category: filter;
  subCategory: filter;
  setPageNo: (pageNo: number) => void;
  setCategory: Dispatch<SetStateAction<filter>>;
  setSubCategory: Dispatch<SetStateAction<filter>>;
  setYear: (year: filter) => void;
  lang: "ar" | "en";
  categories: Categories[];
}

const WinnersContext = createContext<WinnersContextProps>(
  {} as WinnersContextProps,
);

export function useWinnersCtx() {
  const context = useContext(WinnersContext);
  if (!context) {
    throw new Error("useWinners must be used within a WinnersProvider");
  }
  return context;
}

export function WinnersContextProvider({
  children,
  categories,
}: {
  children: ReactNode;
  categories: Categories[];
}) {
  const { lang } = useLocaleCtx();
  const { t } = useTranslation(lang, "winners");
  const params = useSearchParams();

  const years = t("years", {
    returnObjects: true,
  }) as (typeof WINNERS)["years"];

  const initialYear = {
    title: params.get("year") || years[0].title,
    value: params.get("year") || years[0].value,
  };

  // const categories = t("categories", {
  //   returnObjects: true,
  // }) as (typeof WINNERS)["categories"];

  const subcategories = t("subcategories", {
    returnObjects: true,
  }) as (typeof WINNERS)["subcategories"];

  const [loading, setLoading] = useState(false);
  const [winners, setWinners] = useState<Winner[]>([]);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize] = useState(20);
  const [category, setCategory] = useState<filter>(categories?.[0]);
  const [subCategory, setSubCategory] = useState<filter>(subcategories[0]);
  const [year, setYear] = useState<filter>(initialYear);

  useEffect(() => {
    const years = t("years", {
      returnObjects: true,
    }) as (typeof WINNERS)["years"];

    const initialYear = {
      title: params.get("year") || years[0].title,
      value: params.get("year") || years[0].value,
    };

    const subcategories = t("subcategories", {
      returnObjects: true,
    }) as (typeof WINNERS)["subcategories"];

    setYear(initialYear);

    setSubCategory(subcategories[0]);
  }, [lang, t]);

  const fetchMoreWinners = async () => {
    setLoading(true);
    const galleryData = await getGalleryPageWinners({
      lang,
      pageNo,
      pageSize,
      year: year.value,
      category: category.value,
      subCategory: subCategory.value,
    });
    if (galleryData) {
      setWinners(galleryData.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMoreWinners();
  }, [pageNo, lang, year, category, subCategory]);

  return (
    <WinnersContext.Provider
      value={{
        winners,
        loading,
        pageNo,
        pageSize,
        year,
        category,
        subCategory,
        setPageNo,
        setCategory,
        setSubCategory,
        setYear,
        lang,
        categories,
      }}
    >
      {children}
    </WinnersContext.Provider>
  );
}
